import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeads } from '@/hooks/useLeads';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Search, AlertTriangle, Columns3, List } from 'lucide-react';
import { LeadsTableView } from '@/components/leads/LeadsTableView';
import { LeadsBoard } from '@/components/leads/board/LeadsBoard';
import { groupLeadsByStage, stageOf } from '@/components/leads/leadStages';
import { LeadQuickViewSheet } from '@/components/leads/LeadQuickViewSheet';
import { LeadsFilterPanel, FollowUpFilter } from '@/components/leads/LeadsFilterPanel';
import { LeadsDashboard } from '@/components/leads/LeadsDashboard';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useQuickViewNavigation } from '@/hooks/useQuickViewNavigation';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { getFollowUpStatus } from '@/components/leads/FollowUpIndicator';
import { isThisWeek, isToday } from 'date-fns';
import { cn } from '@/lib/utils';

const LEAD_STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'new', label: 'New' },
  { value: 'needs_follow_up', label: 'Follow-up' },
  { value: 'estimate_sent', label: 'Estimate sent' },
  { value: 'estimate_accepted', label: 'Accepted' },
  { value: 'converted', label: 'Booked' },
  { value: 'lost', label: 'Lost' },
];

const ITEMS_PER_PAGE = 20;

type LeadsView = 'board' | 'table';
const VIEW_STORAGE_KEY = 'leads.view';

// Board is the default; the table is remembered per browser once chosen.
function readStoredView(): LeadsView {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === 'table' ? 'table' : 'board';
  } catch {
    return 'board';
  }
}

export default function Leads() {
  const navigate = useNavigate();
  const { now, startOfDay, endOfDay } = useTimezoneUtils();

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery.trim(), 300);
  const effectiveSearch = debouncedSearch.length >= 2 ? debouncedSearch : '';
  const isSearching = effectiveSearch.length > 0;
  const [activeTab, setActiveTab] = useState<string>('all');
  const [view, setView] = useState<LeadsView>(readStoredView);
  const [boardUnassignedOnly, setBoardUnassignedOnly] = useState(false);

  const changeView = (next: LeadsView) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // storage unavailable (private window) — the choice lasts this visit only
    }
  };

  // Date filter states - default to "today" in configured timezone
  const [dateFrom, setDateFrom] = useState<Date | undefined>(() => startOfDay(new Date()));
  const [dateTo, setDateTo] = useState<Date | undefined>(() => endOfDay(new Date()));
  const [selectedCsr, setSelectedCsr] = useState<string>('all');
  const [selectedTech, setSelectedTech] = useState<string>('all');
  const [location, setLocation] = useState('all');
  const [displayLimit, setDisplayLimit] = useState(ITEMS_PER_PAGE);
  const [followUpFilter, setFollowUpFilter] = useState<FollowUpFilter>('all');
  const [compareEnabled, setCompareEnabled] = useState(false);

  // Quick view state
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  const { data: allLeads = [], isLoading } = useLeads('all', effectiveSearch);

  // Reset pagination when filters or search change
  useEffect(() => {
    setDisplayLimit(ITEMS_PER_PAGE);
  }, [activeTab, dateFrom, dateTo, selectedCsr, selectedTech, location, effectiveSearch, followUpFilter]);

  const handleDateChange = (from: Date | undefined, to: Date | undefined) => {
    setDateFrom(from);
    setDateTo(to);
  };

  // Apply filters to leads (without status filter for counting)
  const applyFiltersToLeads = (leads: typeof allLeads, includeStatus?: string) => {
    let filtered: typeof allLeads;

    if (includeStatus === 'unassigned') {
      // Unassigned pool: leads with no assigned CSR, regardless of lead status
      filtered = leads.filter(lead => lead.assigned_csr_id == null);
    } else if (includeStatus && includeStatus !== 'all') {
      filtered = leads.filter(lead => lead.status === includeStatus);
    } else {
      filtered = leads;
    }

    if (dateFrom) {
      filtered = filtered.filter(lead => new Date(lead.created_at) >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(lead => new Date(lead.created_at) <= dateTo);
    }
    // Skip CSR filter on the unassigned tab (all leads there have no CSR by definition)
    if (selectedCsr !== 'all' && includeStatus !== 'unassigned') {
      filtered = filtered.filter(lead => lead.assigned_csr_id === selectedCsr);
    }
    if (selectedTech !== 'all') {
      if (selectedTech === 'unassigned') {
        filtered = filtered.filter(lead => !lead.assigned_tech_id);
      } else {
        filtered = filtered.filter(lead => lead.assigned_tech_id === selectedTech);
      }
    }
    if (location && location !== 'all') {
      filtered = filtered.filter(lead => lead.metro_id === location);
    }

    // Apply follow-up filter
    if (followUpFilter !== 'all') {
      const todayDate = new Date();
      const todayStart = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate());

      filtered = filtered.filter(lead => {
        const status = getFollowUpStatus(lead.next_follow_up_date);

        switch (followUpFilter) {
          case 'overdue':
            return status === 'overdue';
          case 'today':
            return status === 'today';
          case 'this_week':
            if (!lead.next_follow_up_date) return false;
            const date = new Date(lead.next_follow_up_date);
            return isThisWeek(date) && date >= todayStart;
          case 'none':
            return status === 'none';
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  const filteredLeads = useMemo(() =>
    applyFiltersToLeads(allLeads, activeTab),
    [allLeads, activeTab, dateFrom, dateTo, selectedCsr, selectedTech, location, followUpFilter]
  );

  // Board: every stage at once, so the status tab doesn't apply
  const boardLeads = useMemo(() =>
    applyFiltersToLeads(allLeads, boardUnassignedOnly ? 'unassigned' : undefined),
    [allLeads, boardUnassignedOnly, dateFrom, dateTo, selectedCsr, selectedTech, location, followUpFilter]
  );
  const leadsByStage = useMemo(() => groupLeadsByStage(boardLeads), [boardLeads]);
  const boardUnassignedCount = useMemo(
    () => applyFiltersToLeads(allLeads, 'unassigned').length,
    [allLeads, dateFrom, dateTo, selectedCsr, selectedTech, location, followUpFilter]
  );

  // Quick view prev/next walks the table rows, or the open lead's board column
  const navItems = useMemo(() => {
    if (view === 'table') return filteredLeads;
    const selected = selectedLeadId ? allLeads.find(l => l.id === selectedLeadId) : undefined;
    return selected ? leadsByStage[stageOf(selected.status)] : [];
  }, [view, filteredLeads, selectedLeadId, allLeads, leadsByStage]);

  // Quick view navigation
  const { hasPrevious, hasNext, goToPrevious, goToNext } = useQuickViewNavigation({
    items: navItems,
    getItemId: (lead) => lead.id,
    selectedId: selectedLeadId,
    onSelect: setSelectedLeadId,
  });

  const displayedLeads = useMemo(() => {
    return isSearching ? filteredLeads : filteredLeads.slice(0, displayLimit);
  }, [filteredLeads, displayLimit, isSearching]);

  const remainingCount = filteredLeads.length - displayLimit;
  const hasMore = !isSearching && remainingCount > 0;

  const getLeadCount = (status: string) => {
    return applyFiltersToLeads(allLeads, status === 'all' ? undefined : status).length;
  };

  // Total unassigned count across ALL dates (for the badge urgency signal)
  const totalUnassignedCount = useMemo(
    () => allLeads.filter(l => l.assigned_csr_id == null).length,
    [allLeads]
  );

  // Source summary for the page subtitle: leads within the selected date range, by entry type
  const sourceSummary = useMemo(() => {
    const inRange = allLeads.filter(lead => {
      const d = new Date(lead.created_at);
      return (!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo);
    });
    return {
      total: inRange.length,
      phone: inRange.filter(l => l.lead_entry_type === 'webhook').length,
      form: inRange.filter(l => l.lead_entry_type === 'form').length,
      manual: inRange.filter(l => l.lead_entry_type === 'manual').length,
    };
  }, [allLeads, dateFrom, dateTo]);

  const periodLabel = !dateFrom && !dateTo
    ? 'All time'
    : dateFrom && dateTo && isToday(dateFrom) && isToday(dateTo)
      ? 'Today'
      : 'Selected period';

  const handleClearFilters = () => {
    setDateFrom(startOfDay(new Date()));
    setDateTo(endOfDay(new Date()));
    setSelectedCsr('all');
    setSelectedTech('all');
    setLocation('all');
    setSearchQuery('');
    setFollowUpFilter('all');
  };

  const handleFollowUpFilterClick = (filter: 'overdue' | 'today' | 'this_week') => {
    // Clear date filter to see all leads with follow-up
    setDateFrom(undefined);
    setDateTo(undefined);
    setFollowUpFilter(filter);
    // Switch to "all" tab so follow-up results aren't hidden by status filter
    setActiveTab('all');
  };

  const showMoreButton = hasMore ? (
    <div className="flex justify-center border-t py-3">
      <Button
        variant="link"
        className="h-auto p-0 text-[13px] font-semibold text-primary tabular-nums"
        onClick={() => setDisplayLimit(prev => prev + ITEMS_PER_PAGE)}
      >
        Show more ({remainingCount} remaining)
      </Button>
    </div>
  ) : null;

  return (
    <Layout>
      <div className="space-y-[22px]">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Leads</h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {isLoading ? (
                ' '
              ) : (
                <>
                  {periodLabel} ·{' '}
                  <span className="font-semibold text-foreground">{sourceSummary.total} new</span> ·{' '}
                  <span className="font-semibold text-foreground">{sourceSummary.phone}</span> phone calls ·{' '}
                  <span className="font-semibold text-foreground">{sourceSummary.form}</span> form submissions ·{' '}
                  <span className="font-semibold text-foreground">{sourceSummary.manual}</span> manual
                </>
              )}
            </p>
          </div>
          <Button className="h-9 gap-1.5" onClick={() => navigate('/leads/new')}>
            <Plus className="h-4 w-4" />
            New lead
          </Button>
        </div>

        <LeadsDashboard
          dateFrom={dateFrom}
          dateTo={dateTo}
          compareEnabled={compareEnabled}
          onFollowUpFilterClick={handleFollowUpFilterClick}
        />

        {/* Filter row: search + dropdown chips */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, phone, or lead number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 rounded-md pl-9 text-[13px]"
            />
          </div>

          <LeadsFilterPanel
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateChange={handleDateChange}
            selectedCsr={selectedCsr}
            onCsrChange={setSelectedCsr}
            selectedTech={selectedTech}
            onTechChange={setSelectedTech}
            location={location}
            onLocationChange={setLocation}
            onClearFilters={handleClearFilters}
            followUpFilter={followUpFilter}
            onFollowUpFilterChange={setFollowUpFilter}
            compareEnabled={compareEnabled}
            onCompareChange={setCompareEnabled}
          />

          {view === 'board' && boardUnassignedCount > 0 && (
            <button
              type="button"
              onClick={() => setBoardUnassignedOnly(v => !v)}
              className={cn(
                'flex h-9 items-center gap-1.5 rounded-md border px-3 text-[13px] font-medium text-warning transition-colors',
                boardUnassignedOnly ? 'border-warning bg-warning/10' : 'border-warning/40 hover:bg-warning/10',
              )}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              {boardUnassignedOnly ? 'Showing unassigned' : 'Unassigned'}
              <span className="rounded-full bg-warning/10 px-1.5 py-0.5 text-[11px] font-semibold leading-none tabular-nums">
                {boardUnassignedCount}
              </span>
            </button>
          )}

          <div className="flex h-9 items-center rounded-md border p-0.5" role="group" aria-label="Leads view">
            {([
              { value: 'board', label: 'Board', icon: Columns3 },
              { value: 'table', label: 'Table', icon: List },
            ] as const).map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => changeView(value)}
                aria-pressed={view === value}
                className={cn(
                  'flex h-full items-center gap-1.5 rounded px-2.5 text-[13px] font-medium transition-colors',
                  view === value ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {view === 'board' && (
          isLoading ? (
            <div className="py-12 text-center text-muted-foreground">
              Loading leads...
            </div>
          ) : (
            <LeadsBoard
              leadsByStage={leadsByStage}
              onLeadClick={setSelectedLeadId}
              selectedLeadId={selectedLeadId}
            />
          )
        )}

        {/* Table card with underline status tabs in the header */}
        {view === 'table' && (
          <Card className="overflow-hidden">
            <div className="flex items-center gap-1 overflow-x-auto border-b px-3">
              {LEAD_STATUSES.map((status) => {
                const count = getLeadCount(status.value);
                const isActive = activeTab === status.value;
                const showWarning = status.value === 'unassigned' && count > 0;
                return (
                  <button
                    key={status.value}
                    type="button"
                    onClick={() => setActiveTab(status.value)}
                    className={cn(
                      '-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-3 text-[13px] font-medium transition-colors',
                      isActive
                        ? 'border-primary text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                      showWarning && 'text-warning hover:text-warning',
                    )}
                  >
                    {showWarning && <AlertTriangle className="h-3.5 w-3.5 text-warning" />}
                    {status.label}
                    <span
                      className={cn(
                        'rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none tabular-nums',
                        showWarning ? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary',
                      )}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <CardContent className="p-0">
              {activeTab === 'unassigned' && (
                <div className="mx-4 mt-4 flex items-start gap-2.5 rounded-md bg-warning/10 p-3 text-[13px] text-warning">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    These leads have no assigned CSR. Open any lead to claim it, or wait for a CSR to take action — they will be auto-assigned when someone logs a call, note, or sends an SMS.
                  </span>
                </div>
              )}
              {isLoading ? (
                <div className="py-12 text-center text-muted-foreground">
                  Loading leads...
                </div>
              ) : (
                <LeadsTableView
                  leads={displayedLeads}
                  onLeadClick={setSelectedLeadId}
                  selectedLeadId={selectedLeadId}
                  showMoreButton={showMoreButton}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Lead Quick View Sheet */}
        <LeadQuickViewSheet
          leadId={selectedLeadId}
          open={!!selectedLeadId}
          onOpenChange={(open) => !open && setSelectedLeadId(null)}
          onPrevious={hasPrevious ? goToPrevious : undefined}
          onNext={hasNext ? goToNext : undefined}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
        />
      </div>
    </Layout>
  );
}
